import { c as _c } from "react/compiler-runtime";
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ProductTypeEnum, StoreImage, StoreImageEnum, StoreItemEnum } from '@systemeio/digital-store-shared';
import { Modal, ModalSizeEnum, PrimaryButton } from '@systemeio/ui';
import { GrowthbookFlagsEnum } from 'growthbook/types';
import { storeItemDescriptionLocoKeys, storeItemProductDescriptionLocoKeys, storeItemProductTitleLocoKeys, storeItemTitleLocoKeys } from 'modules/stores/store/constants/store-item-constants';
import { StoreTabEnum } from 'modules/stores/store/enum/store-tab-enum';
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query';
import { StoreItemWithTabType } from 'modules/stores/store/types/store-item-interface';
import { getImageTypeFromItem } from 'modules/stores/store/utils/get-image-type-from-item';
import { getTabFromItem } from 'modules/stores/store/utils/get-tab-from-item';
import { FC, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { AddIcon } from '@systemeio/ui/icons';
const getItemCardProps = (itemType: StoreItemWithTabType, onCreateItem: (tab: StoreTabEnum, productType?: ProductTypeEnum) => Promise<boolean>, physicalProductIsOn: boolean): Props[] => {
  switch (itemType) {
    case StoreItemEnum.product:
      {
        const productTypes = Object.values(ProductTypeEnum);
        const filteredProductTypes = physicalProductIsOn ? productTypes : productTypes.filter(productType => productType !== ProductTypeEnum.physicalProduct);
        return filteredProductTypes.map(productType => {
          const imageType = getImageTypeFromItem({
            itemType,
            productType
          });
          const title = storeItemProductTitleLocoKeys[productType];
          const description = storeItemProductDescriptionLocoKeys[productType];
          return {
            title,
            description,
            imageType,
            onCreate: () => onCreateItem(getTabFromItem(itemType), productType),
            key: productType
          };
        });
      }
    default:
      {
        return [{
          title: storeItemTitleLocoKeys[itemType],
          description: storeItemDescriptionLocoKeys[itemType],
          imageType: getImageTypeFromItem({
            itemType
          }),
          onCreate: () => onCreateItem(getTabFromItem(itemType)),
          key: itemType
        }];
      }
  }
};
interface Props {
  imageType: StoreImageEnum;
  title: string;
  description: string;
  onCreate: () => Promise<boolean>;
  key: string;
}
const ItemCard: FC<Props> = t0 => {
  const $ = _c(21);
  const {
    title,
    description,
    imageType,
    onCreate
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] !== t || $[1] !== title) {
    t1 = t(title);
    $[0] = t;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== imageType || $[4] !== t1) {
    t2 = <StoreImage imageType={imageType} alt={t1} skipTheme={true} />;
    $[3] = imageType;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t || $[7] !== title) {
    t3 = t(title);
    $[6] = t;
    $[7] = title;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <b className="text-text-primary">{t3}</b>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== description || $[12] !== t) {
    t5 = t(description);
    $[11] = description;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <p>{t5}</p>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== onCreate || $[17] !== t2 || $[18] !== t4 || $[19] !== t6) {
    t7 = <button className="border-text-secondary/30 hover:border-brand-primary main-transition-colors focus-visible:border-brand-primary text-start flex flex-col gap-3 rounded-lg border p-4 outline-hidden sm:p-6 md:p-8" onClick={onCreate}>{t2}{t4}{t6}</button>;
    $[16] = onCreate;
    $[17] = t2;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
const AddItemModal: FC = () => {
  const $ = _c(21);
  const {
    t
  } = useLocoTranslation();
  const {
    onCreateItem
  } = useStoreQuery();
  const [opened, setOpened] = useState(false);
  const physicalProductFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.DSPhysicalProduct);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setOpened(true);
    t1 = <AddIcon />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("dashboard.store.add_product");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <PrimaryButton onClick={t0}>{t1}<span>{t2}</span></PrimaryButton>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpened(false);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("dashboard.store.add_product");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== onCreateItem || $[10] !== physicalProductFeatureIsOn) {
    t6 = (Object.values(StoreItemEnum).filter(_temp) as StoreItemWithTabType[]).map(itemType_0 => getItemCardProps(itemType_0, onCreateItem, physicalProductFeatureIsOn)).flat().map(_temp2);
    $[9] = onCreateItem;
    $[10] = physicalProductFeatureIsOn;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="grid grid-cols-2 gap-4 md:gap-5">{t6}</div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== opened || $[15] !== t5 || $[16] !== t7) {
    t8 = <Modal size={ModalSizeEnum.medium} opened={opened} onClose={t4} title={t5}>{t7}</Modal>;
    $[14] = opened;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t3 || $[19] !== t8) {
    t9 = <>{t3}{t8}</>;
    $[18] = t3;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
export default AddItemModal;
function _temp(itemType) {
  return itemType !== StoreItemEnum.header;
}
function _temp2(itemProps) {
  const {
    key,
    ...rest
  } = itemProps;
  return <ItemCard key={key} {...rest} />;
}